import { useState, useEffect } from "react";
import * as Yup from "yup";
import ValidationConfiguration from "./ValidationConfiguration";
import ValidationResult from "./ValidationResult";
import ValidationItem from "./ValidationItem";
import ValidationMessage from "./ValidationMessage";

const useChangeTracker = (
  validationConfigurations: ValidationConfiguration[],
  errorsVisible: boolean
) => {
  const [tracker, setTracker] = useState<any>({});
  const [validationResult, setValidationResult] = useState<ValidationResult>(
    new ValidationResult([], errorsVisible)
  );
  const [errorsVisibility, setErrorsVisibility] = useState(errorsVisible);

  useEffect(() => {
    populateValidationResult();
  }, [tracker, errorsVisibility]);

  const updateTracker = (trackerName: string, value: any) => {
    setTracker((previousTracker: any) => ({
      ...previousTracker,
      [trackerName]: value
    }));
  };

  const updateTrackerDebounceEffect = (args: [string, any][]) => {
    const handler = setTimeout(() => {
      args.forEach(arg => {
        updateTracker(arg[0], arg[1]);
      });
    }, 250);

    return () => {
      clearTimeout(handler);
    };
  };

  const populateValidationResult = () => {
    let validationItems: ValidationItem[] = [];

    validationConfigurations.forEach(config => {
      let validationItem = resolveValidationItem(config);
      validationItems.push(validationItem);
    });

    const validationResult = new ValidationResult(
      validationItems,
      errorsVisibility
    );

    setValidationResult(validationResult);
  };

  const resolveValidationItem = (
    config: ValidationConfiguration
  ): ValidationItem => {
    let validationItem = {
      trackerName: config.trackerName,
      messages: []
    } as ValidationItem;

    try {
      config.validationScheme.validateSync(tracker[config.trackerName], {
        abortEarly: false
      });
    } catch (error) {
      const validationError = error as Yup.ValidationError;
      validationError.inner.forEach(innerError => {
        let validationMessage = resolveValidationMessage(innerError);
        validationItem.messages.push(validationMessage);
      });
    }

    return validationItem;
  };

  const resolveValidationMessage = (
    error: Yup.ValidationError
  ): ValidationMessage => {
    let validationMessage = {
      message: error.message,
      objectPath: error.path,
      validationType: error.type
    } as ValidationMessage;

    let pathParts = error.path.split(".");
    pathParts.forEach(pathPart => {
      if (
        pathParts.indexOf(pathPart) === 0 &&
        pathPart.includes("[") &&
        pathPart.includes("]")
      ) {
        validationMessage.index = pathPart.substring(
          pathPart.lastIndexOf("[") + 1,
          pathPart.lastIndexOf("]")
        );
      } else if (pathParts.indexOf(pathPart) === 1 && pathParts.length === 3) {
        validationMessage.parentPropertyName = pathPart;
      } else {
        validationMessage.propertyName = pathPart;
      }
    });

    return validationMessage;
  };

  const showErrors = () => {
    setErrorsVisibility(true);
  };

  const resetTracker = (newValues: [string, any][]) => {
    const newTracker: any = {};
    newValues.forEach(([trackerName, value]) => {
      newTracker[trackerName] = value;
    });
    setTracker(newTracker);
  };
  

  return {
    validationResult,
    updateTracker,
    updateTrackerDebounceEffect,
    showErrors,
    resetTracker
  };
};

export default useChangeTracker;
